import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  HostListener,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'meu-product-filter',
  templateUrl: './product-filter.component.html',
  styleUrls: ['./product-filter.component.scss'],
})
export class ProductFilterComponent implements OnInit, OnDestroy {
  @Input() brands: any[] = [];
  @Input() manufacturers: any[] = [];
  @Input() selectedBrandIds: string[] = [];
  @Input() selectedManufacturerIds: string[] = [];

  @Output() filterChange = new EventEmitter<{
    brandIds: string[];
    manufacturerIds: string[];
  }>();
  @Output() reset = new EventEmitter<void>();
  @Output() toggle = new EventEmitter<void>();

  @ViewChild('filterElement', { static: false }) filterElementRef: ElementRef;

  filter = false;
  showBrandList = false;
  showManufacturerList = false;

  brandSearchInput = '';
  manufacturerSearchInput = '';

  filteredBrands: any[] = [];
  filteredManufacturers: any[] = [];

  brandSearchKeyUp = new Subject<string>();
  manufacturerSearchKeyUp = new Subject<string>();

  private originalFilterParent: HTMLElement;

  constructor(private renderer: Renderer2) {}

  ngOnInit() {
    this.filteredBrands = this.brands;
    this.filteredManufacturers = this.manufacturers;

    this.brandSearchKeyUp.pipe(debounceTime(300)).subscribe((text) => {
      this.filterBrands(text);
    });
    this.manufacturerSearchKeyUp.pipe(debounceTime(300)).subscribe((text) => {
      this.filterManufacturers(text);
    });
  }

  ngOnChanges() {
    this.filteredBrands = this.brands;
    this.filteredManufacturers = this.manufacturers;
  }

  ngOnDestroy() {
    this.brandSearchKeyUp.unsubscribe();
    this.manufacturerSearchKeyUp.unsubscribe();
    if (this.filter) {
      this.closeFilterDrawer();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const width = (event.target as Window).innerWidth;
    if (width > 1024 && this.filter) {
      this.filterShow();
    }
  }

  filterShow() {
    this.filter = !this.filter;
    this.toggle.emit();
    if (this.filter) {
      this.openFilterDrawer();
    } else {
      this.closeFilterDrawer();
    }
  }

  private openFilterDrawer() {
    const filterEl = this.filterElementRef.nativeElement;
    const body = document.body;
    if (!this.originalFilterParent) {
      this.originalFilterParent = filterEl.parentNode;
    }
    this.renderer.appendChild(body, filterEl);
    this.renderer.setStyle(body, 'overflow', 'hidden');
  }

  private closeFilterDrawer() {
    const filterEl = this.filterElementRef.nativeElement;
    this.renderer.removeStyle(document.body, 'overflow');
    if (this.originalFilterParent && filterEl.parentNode === document.body) {
      this.renderer.insertBefore(
        this.originalFilterParent,
        filterEl,
        this.originalFilterParent.firstChild
      );
    } else if (filterEl.parentNode === document.body) {
      this.renderer.removeChild(document.body, filterEl);
    }
  }

  filterBrands(searchText: string) {
    this.showBrandList = true;
    if (!searchText) {
      this.filteredBrands = this.brands;
      return;
    }
    searchText = searchText.toLowerCase().trim();
    this.filteredBrands = this.brands.filter((item) => {
      const name = (item.extend_brand && item.extend_brand.name) || item.name || '';
      return name.toLowerCase().includes(searchText);
    });
  }

  filterManufacturers(searchText: string) {
    this.showManufacturerList = true;
    if (!searchText) {
      this.filteredManufacturers = this.manufacturers;
      return;
    }
    searchText = searchText.toLowerCase().trim();
    this.filteredManufacturers = this.manufacturers.filter((item) => {
      const name = item.name || '';
      return name.toLowerCase().includes(searchText);
    });
  }

  getBrandId(item: any): string {
    return item.brand_id || item.id || '';
  }

  getBrandName(item: any): string {
    return (item.extend_brand && item.extend_brand.name) || item.name || '';
  }

  onBrandCheckChange(id: string, checked: boolean) {
    if (checked) {
      if (!this.selectedBrandIds.includes(id)) {
        this.selectedBrandIds.push(id);
      }
    } else {
      this.selectedBrandIds = this.selectedBrandIds.filter((b) => b !== id);
    }
    this.emitFilterChange();
  }

  onManufacturerCheckChange(id: string, checked: boolean) {
    if (checked) {
      if (!this.selectedManufacturerIds.includes(id)) {
        this.selectedManufacturerIds.push(id);
      }
    } else {
      this.selectedManufacturerIds = this.selectedManufacturerIds.filter((m) => m !== id);
    }
    this.emitFilterChange();
  }

  onReset() {
    this.selectedBrandIds = [];
    this.selectedManufacturerIds = [];
    this.brandSearchInput = '';
    this.manufacturerSearchInput = '';
    this.filteredBrands = this.brands;
    this.filteredManufacturers = this.manufacturers;
    this.showBrandList = false;
    this.showManufacturerList = false;
    this.reset.emit();
  }

  private emitFilterChange() {
    this.filterChange.emit({
      brandIds: this.selectedBrandIds.slice(),
      manufacturerIds: this.selectedManufacturerIds.slice(),
    });
  }
}
